/**
 * Doubloon Sticky Add-to-Cart
 *
 * Shows a fixed bar when the main buy button scrolls out of view.
 * Subscribes to Dawn's variant-change event for live updates.
 * Uses IntersectionObserver for performant scroll detection.
 */

if (!customElements.get('sticky-atc')) {
  class StickyATC extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.variantEl = this.querySelector('[data-sticky-variant]');
      this.priceEl = this.querySelector('[data-sticky-price]');
      this.buttonEl = this.querySelector('[data-sticky-button]');
      this.visible = false;
      this.unsubscribers = [];
    }

    connectedCallback() {
      this.observeBuyButton();
      this.subscribeToVariantChanges();
      this.listenToPersonalizerState();
      this.bindClick();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.unsubscribers.forEach((unsub) => unsub());
      if (this._onPersonalizerState) {
        document.removeEventListener('personalizer-submit-state', this._onPersonalizerState);
      }
    }

    observeBuyButton() {
      // Find the main product form submit button
      const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!productInfo) return;

      const buyButton = productInfo.querySelector('.product-form__submit');
      if (!buyButton) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.hide();
            } else {
              this.show();
            }
          });
        },
        { threshold: 0, rootMargin: '0px' }
      );

      this.observer.observe(buyButton);
    }

    subscribeToVariantChanges() {
      if (typeof subscribe !== 'function') return;

      const unsub = subscribe('variant-change', (event) => {
        if (!event.data) return;
        const { variant } = event.data;
        if (!variant) return;

        this.updateVariant(variant);
      });

      this.unsubscribers.push(unsub);
    }

    updateVariant(variant) {
      if (this.variantEl) {
        this.variantEl.textContent =
          variant.title === 'Default Title' ? '' : variant.title;
      }

      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(variant.price);
      }

      if (this.buttonEl) {
        this.buttonEl.disabled = !variant.available;
        this.buttonEl.textContent = variant.available
          ? (window.variantStrings?.addToCart || 'Add to cart')
          : (window.variantStrings?.soldOut || 'Sold out');
      }
    }

    formatMoney(cents) {
      // Use Shopify's money format if available, otherwise basic formatting
      if (window.Shopify && window.Shopify.formatMoney) {
        return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    listenToPersonalizerState() {
      this._onPersonalizerState = (event) => {
        if (this.buttonEl) {
          this.buttonEl.disabled = event.detail.disabled;
        }
      };
      document.addEventListener('personalizer-submit-state', this._onPersonalizerState);
    }

    bindClick() {
      if (!this.buttonEl) return;

      this.buttonEl.addEventListener('click', () => {
        // Find the real product form and submit it
        const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
        if (!productInfo) return;

        const form = productInfo.querySelector('product-form');
        if (form) {
          const submitButton = form.querySelector('[type="submit"]');
          if (submitButton && !submitButton.disabled) {
            submitButton.click();
          }
        }
      });
    }

    show() {
      if (this.visible) return;
      this.visible = true;
      this.setAttribute('aria-hidden', 'false');
      this.removeAttribute('inert');
      this.classList.add('is-visible');
    }

    hide() {
      if (!this.visible) return;
      this.visible = false;
      this.setAttribute('aria-hidden', 'true');
      this.setAttribute('inert', '');
      this.classList.remove('is-visible');
    }
  }

  customElements.define('sticky-atc', StickyATC);
}
